import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../utils/api';
import { 
  Lock, 
  Eye, 
  EyeOff, 
  Users, 
  FileText, 
  ShoppingBag, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Trash2,
  Download,
  LogOut,
  ChevronDown,
  ChevronUp,
  Image,
  Music,
  ExternalLink,
  Play,
  Instagram,
  Youtube
} from 'lucide-react';

export default function BackOfficePage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('submissions');
  const [adminUser, setAdminUser] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem('admin_token') || '');
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [submissions, setSubmissions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [expandedSubmission, setExpandedSubmission] = useState(null);

  // Check if already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.role === 'admin') {
        setAdminUser(response.data);
        setAuthToken(token);
        setIsAuthenticated(true);
        fetchAllData(token);
      } else {
        toast.error('Admin access required');
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      localStorage.removeItem('admin_token');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginData.email,
        password: loginData.password
      });
      
      const { token, user } = response.data;
      
      if (user.role === 'admin') {
        localStorage.setItem('admin_token', token);
        setAuthToken(token);
        setAdminUser(user);
        setIsAuthenticated(true);
        toast.success('Welcome to the Back Office!');
        fetchAllData(token);
      } else {
        toast.error('Admin access required. Please login with an admin account.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setAdminUser(null);
    setAuthToken('');
    setLoginData({ email: '', password: '' });
    toast.success('Logged out successfully');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If an account exists with this email, you will receive a password reset code.');
      setShowResetForm(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      toast.success('Password reset successfully! You can now login.');
      setShowForgotPassword(false);
      setShowResetForm(false);
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${authToken}`
  });

  const fetchAllData = async (token = authToken) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [submissionsRes, ordersRes, waitlistRes, contactsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/waitlist`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/users`, { headers })
      ]);
      setSubmissions(submissionsRes.data || []);
      setOrders(ordersRes.data || []);
      setWaitlist(waitlistRes.data || []);
      setContacts(contactsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/submissions/${id}`, { status }, { headers: getAuthHeaders() });
      toast.success(`Submission marked as ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update submission');
    }
  };

  const updateSubmissionPaymentStatus = async (id, paymentStatus) => {
    try {
      await axios.patch(`${API}/admin/submissions/${id}`, { payment_status: paymentStatus }, { headers: getAuthHeaders() });
      toast.success(`Payment marked as ${paymentStatus}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}`, { payment_status: status }, { headers: getAuthHeaders() });
      toast.success(`Order marked as ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen py-32 bg-[#1A1412] flex items-center justify-center" data-testid="backoffice-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md px-4"
        >
          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#D97736] to-[#CFB53B] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="text-white" size={32} />
                </div>
                <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#FDFBF7] mb-2">
                  Back Office
                </h1>
                <p className="text-[#A89B95] font-['Outfit']">Admin Portal Access</p>
              </div>

              {showForgotPassword ? (
                <div>
                  <div className="text-center mb-6">
                    <h2 className="text-[#FDFBF7] text-xl font-semibold">
                      {showResetForm ? 'Reset Password' : 'Forgot Password'}
                    </h2>
                    <p className="text-[#A89B95] text-sm mt-2">
                      {showResetForm 
                        ? 'Enter the reset code from your email and your new password.' 
                        : 'Enter your admin email to receive a password reset code.'}
                    </p>
                  </div>

                  {!showResetForm ? (
                    <form onSubmit={handleForgotPassword} className="space-y-5">
                      <div>
                        <Label className="text-[#FDFBF7]">Email</Label>
                        <Input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                          placeholder="Enter your admin email"
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white py-6"
                        disabled={forgotLoading}
                      >
                        {forgotLoading ? 'Sending...' : 'Send Reset Code'}
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handleResetPassword} className="space-y-5">
                      <div>
                        <Label className="text-[#FDFBF7]">Reset Code</Label>
                        <Input
                          value={resetToken}
                          onChange={(e) => setResetToken(e.target.value)}
                          className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                          placeholder="Enter code from email"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-[#FDFBF7]">New Password</Label>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                          placeholder="Enter new password"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-[#FDFBF7]">Confirm Password</Label>
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                          placeholder="Confirm new password"
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white py-6"
                        disabled={forgotLoading}
                      >
                        {forgotLoading ? 'Resetting...' : 'Reset Password'}
                      </Button>
                    </form>
                  )}

                  <button 
                    onClick={() => {
                      setShowForgotPassword(false);
                      setShowResetForm(false);
                      setResetToken('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="w-full mt-4 text-[#D97736] hover:text-[#F08F4D] text-sm"
                  >
                    Back to Login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <Label className="text-[#FDFBF7]">Email</Label>
                    <Input
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7]"
                      placeholder="Enter admin email"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-[#FDFBF7]">Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7] pr-10"
                        placeholder="Enter password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A89B95]"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white py-6"
                    disabled={loading}
                  >
                    {loading ? 'Logging in...' : 'Login to Back Office'}
                  </Button>

                  <button 
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="w-full text-[#D97736] hover:text-[#F08F4D] text-sm"
                  >
                    Forgot Password?
                  </button>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 bg-[#1A1412]" data-testid="backoffice-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#FDFBF7]">
              Back Office Dashboard
            </h1>
            <p className="text-[#A89B95]">
              Welcome, {adminUser?.full_name || adminUser?.email} | Manage submissions, orders, and contacts
            </p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-[#D97736] text-[#D97736] hover:bg-[#D97736] hover:text-white"
          >
            <LogOut size={18} className="mr-2" /> Logout
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D97736]/20 rounded-lg flex items-center justify-center">
                  <FileText className="text-[#D97736]" size={20} />
                </div>
                <div>
                  <p className="text-[#A89B95] text-xs">Submissions</p>
                  <p className="text-[#FDFBF7] text-xl font-bold">{submissions.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="text-green-500" size={20} />
                </div>
                <div>
                  <p className="text-[#A89B95] text-xs">Paid</p>
                  <p className="text-[#FDFBF7] text-xl font-bold">{submissions.filter(s => s.payment_status === 'paid').length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#CFB53B]/20 rounded-lg flex items-center justify-center">
                  <ShoppingBag className="text-[#CFB53B]" size={20} />
                </div>
                <div>
                  <p className="text-[#A89B95] text-xs">Orders</p>
                  <p className="text-[#FDFBF7] text-xl font-bold">{orders.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="text-blue-500" size={20} />
                </div>
                <div>
                  <p className="text-[#A89B95] text-xs">Waitlist</p>
                  <p className="text-[#FDFBF7] text-xl font-bold">{waitlist.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                  <Mail className="text-pink-500" size={20} />
                </div>
                <div>
                  <p className="text-[#A89B95] text-xs">Contacts</p>
                  <p className="text-[#FDFBF7] text-xl font-bold">{contacts.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#2C221F] border-[#3D302C]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Users className="text-purple-500" size={20} />
                </div>
                <div>
                  <p className="text-[#A89B95] text-xs">Users</p>
                  <p className="text-[#FDFBF7] text-xl font-bold">{users.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['submissions', 'paid', 'orders', 'waitlist', 'contacts', 'users'].map((tab) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab 
                ? 'bg-[#D97736] text-white' 
                : 'bg-[#2C221F] text-[#A89B95] hover:text-[#FDFBF7]'
              }
            >
              {tab === 'paid' ? 'Paid Submissions' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Content */}
        <Card className="bg-[#2C221F] border-[#3D302C]">
          <CardContent className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#D97736] border-t-transparent"></div>
                <p className="text-[#A89B95] mt-4">Loading...</p>
              </div>
            ) : (
              <>
                {/* Submissions Tab */}
                {activeTab === 'submissions' && (
                  <div className="space-y-4">
                    <h2 className="text-[#FDFBF7] text-xl font-bold mb-4">Artist/Model Submissions</h2>
                    {submissions.length === 0 ? (
                      <p className="text-[#A89B95] text-center py-8">No submissions yet</p>
                    ) : (
                      submissions.map((sub) => (
                        <div key={sub.id || sub.email} className="bg-[#1A1412] rounded-lg border border-[#3D302C] overflow-hidden">
                          {/* Submission Header */}
                          <div className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="text-[#FDFBF7] font-semibold text-lg">{sub.full_name || sub.name}</h3>
                                  <Badge className={
                                    sub.status === 'approved' ? 'bg-green-500' :
                                    sub.status === 'rejected' ? 'bg-red-500' : 'bg-[#CFB53B]'
                                  }>
                                    {sub.status || 'pending'}
                                  </Badge>
                                </div>
                                <p className="text-[#D97736] text-sm capitalize">{sub.submission_type} Submission</p>
                                <div className="flex items-center gap-4 mt-2 text-sm text-[#A89B95]">
                                  <span className="flex items-center gap-1"><Mail size={14} /> {sub.email}</span>
                                  {sub.phone && <span className="flex items-center gap-1"><Phone size={14} /> {sub.phone}</span>}
                                </div>
                                {sub.payment_method && (
                                  <p className="text-[#CFB53B] text-xs mt-2">
                                    Payment: {sub.payment_method} ({sub.payment_status || 'pending'})
                                  </p>
                                )}
                                {sub.created_at && (
                                  <p className="text-[#6B5B54] text-xs mt-1">
                                    <Calendar size={12} className="inline mr-1" />
                                    {formatDate(sub.created_at)}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col gap-2 items-end">
                                <div className="flex gap-1">
                                  {sub.payment_status !== 'paid' && (
                                    <button 
                                      onClick={() => updateSubmissionPaymentStatus(sub.id, 'paid')}
                                      className="p-2 text-[#CFB53B] hover:bg-[#CFB53B]/10 rounded"
                                      title="Mark as Paid"
                                    >
                                      <DollarSign size={20} />
                                    </button>
                                  )}
                                  <button 
                                    onClick={() => updateSubmissionStatus(sub.id, 'approved')}
                                    className="p-2 text-green-500 hover:bg-green-500/10 rounded"
                                    title="Approve"
                                  >
                                    <CheckCircle size={20} />
                                  </button>
                                  <button 
                                    onClick={() => updateSubmissionStatus(sub.id, 'rejected')}
                                    className="p-2 text-red-500 hover:bg-red-500/10 rounded"
                                    title="Reject"
                                  >
                                    <XCircle size={20} />
                                  </button>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)}
                                  className="text-[#D97736] hover:bg-[#D97736]/10"
                                >
                                  {expandedSubmission === sub.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                  <span className="ml-1 text-sm">View Details</span>
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Expanded Details */}
                          <AnimatePresence>
                            {expandedSubmission === sub.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="border-t border-[#3D302C]"
                              >
                                <div className="p-4 bg-[#2C221F] space-y-4">
                                  {/* Bio Text */}
                                  {sub.bio_text && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <FileText size={16} /> Bio
                                      </h4>
                                      <p className="text-[#FDFBF7] bg-[#1A1412] p-3 rounded-lg whitespace-pre-wrap text-sm">
                                        {sub.bio_text}
                                      </p>
                                    </div>
                                  )}

                                  {/* Social Media Links */}
                                  {sub.social_media && Object.values(sub.social_media).some(v => v) && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <ExternalLink size={16} /> Social Media
                                      </h4>
                                      <div className="flex flex-wrap gap-2">
                                        {sub.social_media.instagram && (
                                          <a href={sub.social_media.instagram} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-1 bg-[#1A1412] px-3 py-1 rounded text-sm text-pink-400 hover:bg-pink-500/10">
                                            <Instagram size={14} /> Instagram
                                          </a>
                                        )}
                                        {sub.social_media.twitter && (
                                          <a href={sub.social_media.twitter} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-1 bg-[#1A1412] px-3 py-1 rounded text-sm text-blue-400 hover:bg-blue-500/10">
                                            <ExternalLink size={14} /> Twitter/X
                                          </a>
                                        )}
                                        {sub.social_media.youtube && (
                                          <a href={sub.social_media.youtube} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-1 bg-[#1A1412] px-3 py-1 rounded text-sm text-red-400 hover:bg-red-500/10">
                                            <Youtube size={14} /> YouTube
                                          </a>
                                        )}
                                        {sub.social_media.spotify && (
                                          <a href={sub.social_media.spotify} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-1 bg-[#1A1412] px-3 py-1 rounded text-sm text-green-400 hover:bg-green-500/10">
                                            <Music size={14} /> Spotify
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Photos */}
                                  {sub.photo_files && sub.photo_files.length > 0 && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <Image size={16} /> Photos ({sub.photo_files.length})
                                      </h4>
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {sub.photo_files.map((photo) => (
                                          <div key={photo.id} className="relative group">
                                            <img 
                                              src={`${API}${photo.url}?auth=${authToken}`}
                                              alt={photo.filename}
                                              className="w-full h-32 object-cover rounded-lg border border-[#3D302C]"
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                              }}
                                            />
                                            <div className="hidden w-full h-32 bg-[#1A1412] rounded-lg border border-[#3D302C] items-center justify-center text-[#A89B95]">
                                              <Image size={24} />
                                            </div>
                                            <a 
                                              href={`${API}${photo.url}?auth=${authToken}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg"
                                            >
                                              <Eye size={24} className="text-white" />
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Music Samples */}
                                  {sub.music_files && sub.music_files.length > 0 && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <Music size={16} /> Music Samples ({sub.music_files.length})
                                      </h4>
                                      <div className="space-y-2">
                                        {sub.music_files.map((audio) => (
                                          <div key={audio.id} className="bg-[#1A1412] p-3 rounded-lg flex items-center gap-3">
                                            <div className="w-10 h-10 bg-[#D97736]/20 rounded-lg flex items-center justify-center">
                                              <Play size={18} className="text-[#D97736]" />
                                            </div>
                                            <div className="flex-1">
                                              <p className="text-[#FDFBF7] text-sm">{audio.filename}</p>
                                              <audio 
                                                controls 
                                                className="w-full mt-2 h-8"
                                                src={`${API}${audio.url}?auth=${authToken}`}
                                              >
                                                Your browser does not support audio.
                                              </audio>
                                            </div>
                                            <a 
                                              href={`${API}${audio.url}?auth=${authToken}`}
                                              download={audio.filename}
                                              className="p-2 text-[#D97736] hover:bg-[#D97736]/10 rounded"
                                              title="Download"
                                            >
                                              <Download size={18} />
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Bio File */}
                                  {sub.bio_file && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <FileText size={16} /> Bio File
                                      </h4>
                                      <a 
                                        href={`${API}${sub.bio_file.url}?auth=${authToken}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 bg-[#1A1412] px-4 py-2 rounded-lg text-[#D97736] hover:bg-[#D97736]/10"
                                      >
                                        <Download size={16} />
                                        {sub.bio_file.filename}
                                      </a>
                                    </div>
                                  )}

                                  {/* No files message */}
                                  {!sub.photo_files?.length && !sub.music_files?.length && !sub.bio_file && (
                                    <p className="text-[#A89B95] text-sm italic">No files uploaded with this submission.</p>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Paid Submissions Tab */}
                {activeTab === 'paid' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-[#FDFBF7] text-xl font-bold">Paid Submissions</h2>
                      <Badge className="bg-green-500/20 text-green-400">
                        {submissions.filter(s => s.payment_status === 'paid').length} Paid
                      </Badge>
                    </div>
                    {submissions.filter(s => s.payment_status === 'paid').length === 0 ? (
                      <p className="text-[#A89B95] text-center py-8">No paid submissions yet</p>
                    ) : (
                      submissions.filter(s => s.payment_status === 'paid').map((sub) => (
                        <div key={sub.id || sub.email} className="bg-[#1A1412] rounded-lg border border-green-500/30 overflow-hidden">
                          {/* Submission Header */}
                          <div className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="text-[#FDFBF7] font-semibold text-lg">{sub.full_name || sub.name}</h3>
                                  <Badge className="bg-green-500">PAID</Badge>
                                  <Badge className={
                                    sub.status === 'approved' ? 'bg-blue-500' :
                                    sub.status === 'rejected' ? 'bg-red-500' : 'bg-[#CFB53B]'
                                  }>
                                    {sub.status || 'pending review'}
                                  </Badge>
                                </div>
                                <p className="text-[#D97736] text-sm capitalize">{sub.submission_type} Submission</p>
                                <div className="flex items-center gap-4 mt-2 text-sm text-[#A89B95]">
                                  <span className="flex items-center gap-1"><Mail size={14} /> {sub.email}</span>
                                  {sub.phone && <span className="flex items-center gap-1"><Phone size={14} /> {sub.phone}</span>}
                                </div>
                                <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                                  <DollarSign size={12} /> Payment verified via {sub.payment_method || 'manual'}
                                </p>
                                {sub.created_at && (
                                  <p className="text-[#6B5B54] text-xs mt-1">
                                    <Calendar size={12} className="inline mr-1" />
                                    {formatDate(sub.created_at)}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col gap-2 items-end">
                                <div className="flex gap-1">
                                  <button 
                                    onClick={() => updateSubmissionStatus(sub.id, 'approved')}
                                    className="p-2 text-green-500 hover:bg-green-500/10 rounded"
                                    title="Approve"
                                  >
                                    <CheckCircle size={20} />
                                  </button>
                                  <button 
                                    onClick={() => updateSubmissionStatus(sub.id, 'rejected')}
                                    className="p-2 text-red-500 hover:bg-red-500/10 rounded"
                                    title="Reject"
                                  >
                                    <XCircle size={20} />
                                  </button>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)}
                                  className="text-[#D97736] hover:bg-[#D97736]/10"
                                >
                                  {expandedSubmission === sub.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                  <span className="ml-1 text-sm">View Files</span>
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Expanded Details */}
                          <AnimatePresence>
                            {expandedSubmission === sub.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="border-t border-green-500/30"
                              >
                                <div className="p-4 bg-[#2C221F] space-y-4">
                                  {/* Bio Text */}
                                  {sub.bio_text && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <FileText size={16} /> Bio
                                      </h4>
                                      <p className="text-[#FDFBF7] bg-[#1A1412] p-3 rounded-lg whitespace-pre-wrap text-sm">
                                        {sub.bio_text}
                                      </p>
                                    </div>
                                  )}

                                  {/* Social Media Links */}
                                  {sub.social_media && Object.values(sub.social_media).some(v => v) && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <ExternalLink size={16} /> Social Media
                                      </h4>
                                      <div className="flex flex-wrap gap-2">
                                        {sub.social_media.instagram && (
                                          <a href={sub.social_media.instagram} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-1 bg-[#1A1412] px-3 py-1 rounded text-sm text-pink-400 hover:bg-pink-500/10">
                                            <Instagram size={14} /> Instagram
                                          </a>
                                        )}
                                        {sub.social_media.twitter && (
                                          <a href={sub.social_media.twitter} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-1 bg-[#1A1412] px-3 py-1 rounded text-sm text-blue-400 hover:bg-blue-500/10">
                                            <ExternalLink size={14} /> Twitter/X
                                          </a>
                                        )}
                                        {sub.social_media.youtube && (
                                          <a href={sub.social_media.youtube} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-1 bg-[#1A1412] px-3 py-1 rounded text-sm text-red-400 hover:bg-red-500/10">
                                            <Youtube size={14} /> YouTube
                                          </a>
                                        )}
                                        {sub.social_media.spotify && (
                                          <a href={sub.social_media.spotify} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-1 bg-[#1A1412] px-3 py-1 rounded text-sm text-green-400 hover:bg-green-500/10">
                                            <Music size={14} /> Spotify
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Photos */}
                                  {sub.photo_files && sub.photo_files.length > 0 && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <Image size={16} /> Photos ({sub.photo_files.length})
                                      </h4>
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {sub.photo_files.map((photo) => (
                                          <div key={photo.id} className="relative group">
                                            <img 
                                              src={`${API}${photo.url}?auth=${authToken}`}
                                              alt={photo.filename}
                                              className="w-full h-32 object-cover rounded-lg border border-[#3D302C]"
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                              }}
                                            />
                                            <div className="hidden w-full h-32 bg-[#1A1412] rounded-lg border border-[#3D302C] items-center justify-center text-[#A89B95]">
                                              <Image size={24} />
                                            </div>
                                            <a 
                                              href={`${API}${photo.url}?auth=${authToken}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg"
                                            >
                                              <Eye size={24} className="text-white" />
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Music Samples */}
                                  {sub.music_files && sub.music_files.length > 0 && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <Music size={16} /> Music Samples ({sub.music_files.length})
                                      </h4>
                                      <div className="space-y-2">
                                        {sub.music_files.map((audio) => (
                                          <div key={audio.id} className="bg-[#1A1412] p-3 rounded-lg flex items-center gap-3">
                                            <div className="w-10 h-10 bg-[#D97736]/20 rounded-lg flex items-center justify-center">
                                              <Play size={18} className="text-[#D97736]" />
                                            </div>
                                            <div className="flex-1">
                                              <p className="text-[#FDFBF7] text-sm">{audio.filename}</p>
                                              <audio 
                                                controls 
                                                className="w-full mt-2 h-8"
                                                src={`${API}${audio.url}?auth=${authToken}`}
                                              >
                                                Your browser does not support audio.
                                              </audio>
                                            </div>
                                            <a 
                                              href={`${API}${audio.url}?auth=${authToken}`}
                                              download={audio.filename}
                                              className="p-2 text-[#D97736] hover:bg-[#D97736]/10 rounded"
                                              title="Download"
                                            >
                                              <Download size={18} />
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Bio File */}
                                  {sub.bio_file && (
                                    <div>
                                      <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                        <FileText size={16} /> Bio File
                                      </h4>
                                      <a 
                                        href={`${API}${sub.bio_file.url}?auth=${authToken}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 bg-[#1A1412] px-4 py-2 rounded-lg text-[#D97736] hover:bg-[#D97736]/10"
                                      >
                                        <Download size={16} />
                                        {sub.bio_file.filename}
                                      </a>
                                    </div>
                                  )}

                                  {/* No files message */}
                                  {!sub.photo_files?.length && !sub.music_files?.length && !sub.bio_file && (
                                    <p className="text-[#A89B95] text-sm italic">No files uploaded with this submission.</p>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Orders Tab */}
                {activeTab === 'orders' && (
                  <div className="space-y-4">
                    <h2 className="text-[#FDFBF7] text-xl font-bold mb-4">Shop Orders</h2>
                    {orders.length === 0 ? (
                      <p className="text-[#A89B95] text-center py-8">No orders yet</p>
                    ) : (
                      orders.map((order) => (
                        <div key={order.order_id} className="bg-[#1A1412] rounded-lg p-4 border border-[#3D302C]">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-[#FDFBF7] font-semibold">Order #{order.order_id}</h3>
                              <p className="text-[#D97736]">{order.customer?.name}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-[#A89B95]">
                                <span className="flex items-center gap-1"><Mail size={14} /> {order.customer?.email}</span>
                                <span className="flex items-center gap-1"><DollarSign size={14} /> ${order.total?.toFixed(2)}</span>
                              </div>
                              <div className="mt-2 text-sm text-[#A89B95]">
                                Items: {order.items?.map(i => `${i.name} x${i.quantity}`).join(', ')}
                              </div>
                              {order.created_at && (
                                <p className="text-[#6B5B54] text-xs mt-2">
                                  <Calendar size={12} className="inline mr-1" />
                                  {formatDate(order.created_at)}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col gap-2">
                              <Badge className={
                                order.payment_status === 'paid' ? 'bg-green-500' :
                                order.payment_status === 'cancelled' ? 'bg-red-500' : 'bg-[#CFB53B]'
                              }>
                                {order.payment_status || 'pending'}
                              </Badge>
                              <div className="flex gap-1">
                                <button 
                                  onClick={() => updateOrderStatus(order.order_id, 'paid')}
                                  className="p-1 text-green-500 hover:bg-green-500/10 rounded"
                                  title="Mark as Paid"
                                >
                                  <CheckCircle size={18} />
                                </button>
                                <button 
                                  onClick={() => updateOrderStatus(order.order_id, 'cancelled')}
                                  className="p-1 text-red-500 hover:bg-red-500/10 rounded"
                                  title="Cancel"
                                >
                                  <XCircle size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Waitlist Tab */}
                {activeTab === 'waitlist' && (
                  <div className="space-y-4">
                    <h2 className="text-[#FDFBF7] text-xl font-bold mb-4">Vinyl Pre-Order Waitlist</h2>
                    {waitlist.length === 0 ? (
                      <p className="text-[#A89B95] text-center py-8">No waitlist entries yet</p>
                    ) : (
                      waitlist.map((entry) => (
                        <div key={entry.id || entry.email} className="bg-[#1A1412] rounded-lg p-4 border border-[#3D302C]">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-[#FDFBF7] font-semibold">{entry.name}</h3>
                              <div className="flex items-center gap-4 mt-1 text-sm text-[#A89B95]">
                                <span className="flex items-center gap-1"><Mail size={14} /> {entry.email}</span>
                                {entry.phone && <span className="flex items-center gap-1"><Phone size={14} /> {entry.phone}</span>}
                                <span>Qty: {entry.quantity}</span>
                              </div>
                              {entry.created_at && (
                                <p className="text-[#6B5B54] text-xs mt-2">
                                  <Calendar size={12} className="inline mr-1" />
                                  {formatDate(entry.created_at)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Contacts Tab */}
                {activeTab === 'contacts' && (
                  <div className="space-y-4">
                    <h2 className="text-[#FDFBF7] text-xl font-bold mb-4">Contact Form Submissions</h2>
                    {contacts.length === 0 ? (
                      <p className="text-[#A89B95] text-center py-8">No contact submissions yet</p>
                    ) : (
                      contacts.map((contact) => (
                        <div key={contact.id || contact.email} className="bg-[#1A1412] rounded-lg p-4 border border-[#3D302C]">
                          <div>
                            <h3 className="text-[#FDFBF7] font-semibold">{contact.name}</h3>
                            <p className="text-[#D97736] text-sm">{contact.subject}</p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-[#A89B95]">
                              <span className="flex items-center gap-1"><Mail size={14} /> {contact.email}</span>
                            </div>
                            <p className="text-[#A89B95] text-sm mt-2 bg-[#2C221F] p-3 rounded">{contact.message}</p>
                            {contact.created_at && (
                              <p className="text-[#6B5B54] text-xs mt-2">
                                <Calendar size={12} className="inline mr-1" />
                                {formatDate(contact.created_at)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div className="space-y-4">
                    <h2 className="text-[#FDFBF7] text-xl font-bold mb-4">All Users</h2>
                    {users.length === 0 ? (
                      <p className="text-[#A89B95] text-center py-8">No users yet</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-[#3D302C]">
                              <th className="text-left text-[#A89B95] py-3 px-4 font-medium">Name</th>
                              <th className="text-left text-[#A89B95] py-3 px-4 font-medium">Email</th>
                              <th className="text-left text-[#A89B95] py-3 px-4 font-medium">Role</th>
                              <th className="text-left text-[#A89B95] py-3 px-4 font-medium">Status</th>
                              <th className="text-left text-[#A89B95] py-3 px-4 font-medium">Verified</th>
                              <th className="text-left text-[#A89B95] py-3 px-4 font-medium">Joined</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user) => (
                              <tr key={user.id || user.email} className="border-b border-[#3D302C]/50 hover:bg-[#1A1412]">
                                <td className="py-3 px-4">
                                  <span className="text-[#FDFBF7]">{user.full_name || 'N/A'}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-[#FDFBF7]">{user.email}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <Badge className={
                                    user.role === 'admin' ? 'bg-purple-500/20 text-purple-300' :
                                    user.role === 'artist' ? 'bg-[#D97736]/20 text-[#D97736]' :
                                    'bg-blue-500/20 text-blue-300'
                                  }>
                                    {user.role}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4">
                                  <Badge className={
                                    user.status === 'active' ? 'bg-green-500/20 text-green-300' :
                                    user.status === 'pending_verification' ? 'bg-yellow-500/20 text-yellow-300' :
                                    'bg-red-500/20 text-red-300'
                                  }>
                                    {user.status}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4">
                                  {user.email_verified ? (
                                    <CheckCircle size={18} className="text-green-500" />
                                  ) : (
                                    <XCircle size={18} className="text-red-500" />
                                  )}
                                </td>
                                <td className="py-3 px-4 text-[#A89B95] text-sm">
                                  {user.created_at ? formatDate(user.created_at) : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
